import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { DictationTextarea } from "@/components/ui/DictationTextarea";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  ChevronLeft,
  Mail,
  Phone,
  Pencil,
  Plus,
  Trash2,
  Receipt,
  CheckCircle2,
  XCircle,
  RotateCcw,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/ui/StatusPill";
import { ActionMenu, type ActionMenuItem } from "@/components/ui/ActionMenu";
import { Modal } from "@/components/ui/Modal";
import { ProspectForm } from "@/components/prospects/ProspectForm";
import { CallBrief, type IssueHandler } from "@/components/prospects/CallBrief";
import { OutcomeModal } from "@/components/prospects/OutcomeModal";
import { CloseSaleForm } from "@/components/sales/CloseSaleForm";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import {
  cn,
  formatDate,
  formatDateLong,
  formatEUR,
  fullName,
  initials,
} from "@/lib/utils";
import {
  INTERACTION_KINDS,
  SETTINGS_DEFAULTS,
  type GoogleEvent,
  type Interaction,
  type Prospect,
  type Sale,
  type WebinarAttendee,
} from "@shared/schema";
import { INTERACTION_KIND_LABELS } from "@shared/labels";
import { PRODUCTS } from "@shared/products";
import type { OutcomeChoice } from "@shared/outcome";

export function ProspectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const prospectId = Number(id);
  // `?event=<id>` (lignes d'Aujourd'hui) : RDV de la fiche et de l'issue, s'il est à ce prospect.
  const [searchParams] = useSearchParams();
  const eventParam = Number(searchParams.get("event"));
  // Ouverte depuis la file d'Aujourd'hui : le retour mobile y ramène.
  const fromQueue = searchParams.has("event");
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [attendee, setAttendee] = useState<WebinarAttendee | null>(null);
  const [events, setEvents] = useState<GoogleEvent[]>([]);
  const [bookingUrl, setBookingUrl] = useState<string>(SETTINGS_DEFAULTS.calendly_booking_url);
  const [webinarHeldAt, setWebinarHeldAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [issue, setIssue] = useState<{
    prospect: Prospect;
    eventId?: number;
    choice?: OutcomeChoice;
  } | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    if (!Number.isFinite(prospectId)) return;
    if (!opts?.silent) setLoading(true);
    try {
      const data = await api.getProspect(prospectId);
      setProspect(data.prospect);
      setInteractions(data.interactions);
      setSales(data.sales);
      setAttendee(data.attendee);
      setEvents(data.events);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [prospectId, toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Lien de réservation des messages : utile mais pas bloquant.
  useEffect(() => {
    api
      .settings()
      .then((r) => {
        if (r.settings.calendly_booking_url) setBookingUrl(r.settings.calendly_booking_url);
      })
      .catch(() => {});
  }, []);

  // Jour du live de l'inscrit, pour « Au live du … ».
  const attendeeWebinarId = attendee?.webinarId ?? null;
  useEffect(() => {
    if (attendeeWebinarId === null) {
      setWebinarHeldAt(null);
      return;
    }
    let active = true;
    api
      .listWebinars()
      .then((r) => {
        if (active) {
          setWebinarHeldAt(r.webinars.find((w) => w.id === attendeeWebinarId)?.heldAt ?? null);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [attendeeWebinarId]);

  if (loading || !prospect) {
    return (
      <div className="min-h-64 grid place-items-center">
        <div className="h-8 w-8 rounded-full border-2 border-[var(--color-navy-600)] border-r-transparent animate-spin" />
      </div>
    );
  }

  // RDV de la fiche : celui du lien `?event=` s'il est à ce prospect, sinon le prochain à venir,
  // sinon le plus récent (annulés écartés).
  const nowMs = Date.now();
  const startMs = (e: GoogleEvent) => new Date(e.startTime).getTime();
  const liveEvents = events
    .filter((e) => e.status === "confirmed" && !Number.isNaN(startMs(e)))
    .sort((a, b) => startMs(b) - startMs(a));
  const upcoming = liveEvents
    .filter((e) => startMs(e) >= nowMs)
    .sort((a, b) => startMs(a) - startMs(b))[0];
  const linkedEvent =
    Number.isInteger(eventParam) && eventParam > 0
      ? (events.find((e) => e.id === eventParam && e.prospectId === prospect.id) ?? null)
      : null;
  const briefEvent = linkedEvent ?? upcoming ?? liveEvents[0] ?? null;
  const showBrief = Boolean(attendee) || liveEvents.length > 0 || linkedEvent !== null;
  const hadAttendedRdv = events.some(
    (e) => e.outcome === "attended" && !Number.isNaN(startMs(e)) && startMs(e) <= nowMs,
  );
  const onIssue: IssueHandler = (p, eventId, choice) => setIssue({ prospect: p, eventId, choice });
  const issueEventStart =
    issue?.eventId !== undefined
      ? (events.find((e) => e.id === issue.eventId)?.startTime ?? null)
      : null;

  const isClosed =
    prospect.status === "closed_won" || prospect.status === "rembourse";
  const isLost = prospect.status === "closed_lost";

  async function markLost() {
    if (!prospect) return;
    if (!confirm(`Marquer ${fullName(prospect)} comme perdu ?`)) return;
    try {
      await api.updateProspect(prospect.id, {
        status: "closed_lost",
        nextFollowUpAt: null,
        nextAction: null,
      });
      toast.danger("Prospect marqué perdu");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  async function reactivate() {
    if (!prospect) return;
    try {
      await api.updateProspect(prospect.id, { status: "en_discussion" });
      toast.success("Prospect réactivé");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  async function deleteProspect() {
    if (!prospect) return;
    if (!confirm(`Supprimer définitivement ${fullName(prospect)} ?`)) return;
    await api.deleteProspect(prospect.id);
    toast.success("Prospect supprimé");
    navigate("/prospects");
  }

  // Menu « ··· » (iPhone) : mêmes actions que les boutons du PageHeader, plus Supprimer.
  const menuItems: ActionMenuItem[] = [
    { label: "Éditer", icon: Pencil, onSelect: () => setEditOpen(true) },
    ...(isLost ? [{ label: "Réactiver", icon: RotateCcw, onSelect: reactivate }] : []),
    ...(!isClosed
      ? [
          {
            label: "Closer la vente",
            icon: CheckCircle2,
            onSelect: () => setCloseOpen(true),
            tone: "success" as const,
          },
        ]
      : []),
    ...(!isClosed && !isLost
      ? [{ label: "Marquer perdu", icon: XCircle, onSelect: markLost, tone: "danger" as const }]
      : []),
    { label: "Supprimer", icon: Trash2, onSelect: deleteProspect, tone: "danger" },
  ];

  return (
    <>
      <div className="mb-4 max-sm:flex max-sm:items-center max-sm:justify-between">
        <Link
          to="/prospects"
          className="inline-flex items-center gap-1.5 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-navy-700)] max-sm:hidden"
        >
          <ArrowLeft size={14} />
          <span>Retour aux prospects</span>
        </Link>
        <Link
          to={fromQueue ? "/aujourdhui" : "/prospects"}
          className="sm:hidden inline-flex items-center gap-1.5 h-11 text-base text-[var(--color-muted-foreground)] hover:text-[var(--color-navy-700)]"
        >
          <ChevronLeft size={22} aria-hidden />
          <span className="-ml-1">{fromQueue ? "Aujourd'hui" : "Prospects"}</span>
        </Link>
        <div className="sm:hidden">
          <ActionMenu items={menuItems} />
        </div>
      </div>

      <PageHeader
        title={fullName(prospect)}
        subtitle={
          prospect.source ? `Source : ${prospect.source}` : undefined
        }
        actionsClassName="max-sm:hidden"
        actions={
          <>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setEditOpen(true)}
            >
              <Pencil size={16} /> Éditer
            </button>
            {isLost && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={reactivate}
              >
                <RotateCcw size={16} /> Réactiver
              </button>
            )}
            {!isClosed && !isLost && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={markLost}
              >
                <XCircle size={16} /> Marquer perdu
              </button>
            )}
            {!isClosed && (
              <button
                type="button"
                className="btn btn-success"
                onClick={() => setCloseOpen(true)}
              >
                <CheckCircle2 size={16} /> Closer la vente
              </button>
            )}
          </>
        }
      />

      {showBrief && (
        <div className="mb-4 lg:mb-6">
          <CallBrief
            prospect={prospect}
            attendee={attendee}
            event={briefEvent}
            bookingUrl={bookingUrl}
            webinarHeldAt={webinarHeldAt}
            onIssue={onIssue}
            hideName
            hadAttendedRdv={hadAttendedRdv}
            lastSaleProduct={sales[0]?.product ?? null}
            onNotesSaved={(_, briefNotes) =>
              setProspect((p) => (p ? { ...p, briefNotes } : p))
            }
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="lg:col-span-1 space-y-4">
          <section className="surface p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("avatar w-12 h-12 text-base", showBrief && "max-sm:hidden")}>
                {initials(fullName(prospect))}
              </div>
              <div className="min-w-0">
                <div
                  className={cn(
                    "font-semibold text-[var(--color-navy-700)] truncate",
                    showBrief && "max-sm:hidden",
                  )}
                >
                  {fullName(prospect)}
                </div>
                <StatusPill
                  status={prospect.status}
                  className={cn("mt-1", showBrief && "max-sm:mt-0")}
                />
              </div>
            </div>
            <div className="space-y-3 text-sm">
              {prospect.email && (
                <div className="flex items-center gap-2.5">
                  <Mail
                    size={14}
                    className="text-[var(--color-muted-foreground)]"
                  />
                  <a
                    href={`mailto:${prospect.email}`}
                    className="text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] truncate"
                  >
                    {prospect.email}
                  </a>
                </div>
              )}
              {prospect.phone && (
                <div className="flex items-center gap-2.5">
                  <Phone
                    size={14}
                    className="text-[var(--color-muted-foreground)]"
                  />
                  <a
                    href={`tel:${prospect.phone.replace(/\s+/g, "")}`}
                    className="text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] tabular"
                  >
                    {prospect.phone}
                  </a>
                </div>
              )}
              {prospect.nextFollowUpAt && (
                <div className="flex items-center gap-2.5">
                  <Calendar
                    size={14}
                    className="text-[var(--color-muted-foreground)]"
                  />
                  <span className="text-[var(--color-navy-700)]">
                    Relance le {formatDate(prospect.nextFollowUpAt)}
                  </span>
                </div>
              )}
              {prospect.productInterest && (
                <div className="flex items-center gap-2.5">
                  <Receipt
                    size={14}
                    className="text-[var(--color-muted-foreground)]"
                  />
                  <span className="text-[var(--color-navy-700)]">
                    {PRODUCTS[prospect.productInterest].label}
                  </span>
                </div>
              )}
            </div>
            {prospect.context && (
              <div className="mt-5 pt-5 border-t border-[var(--color-border)]">
                <h3 className="text-xs uppercase tracking-wide font-medium text-[var(--color-muted-foreground)] mb-2">
                  Contexte
                </h3>
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-[var(--color-foreground)]">
                  {prospect.context}
                </p>
              </div>
            )}
            <button
              type="button"
              className={cn("btn btn-danger w-full mt-5", showBrief && "max-sm:hidden")}
              onClick={deleteProspect}
            >
              <Trash2 size={14} /> Supprimer
            </button>
          </section>

          {sales.length > 0 && (
            <section className="surface p-5">
              <h3 className="text-sm uppercase tracking-wide font-medium text-[var(--color-muted-foreground)] mb-3">
                Ventes
              </h3>
              <ul className="space-y-3">
                {sales.map((s) => (
                  <li
                    key={s.id}
                    className="bg-[var(--color-navy-50)] border border-[var(--color-navy-100)] rounded-[var(--radius-md)] p-3"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-medium text-[var(--color-navy-700)]">
                        {PRODUCTS[s.product].label}
                      </span>
                      <span className="font-semibold tabular text-[var(--color-navy-700)]">
                        {formatEUR(s.totalAmount)}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--color-muted-foreground)]">
                      Close le {formatDate(s.closedAt)} ·{" "}
                      {s.paymentPlan === 1 ? "1x" : `${s.paymentPlan}x`}
                    </div>
                    <div className="text-xs text-[var(--color-coral-600)] font-medium mt-1">
                      Commission :{" "}
                      {formatEUR(s.totalAmount * s.commissionRate)}
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/ventes"
                className="btn btn-outline btn-sm w-full mt-3"
              >
                Voir l'échéancier
              </Link>
            </section>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <section className="surface p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">
                Timeline
              </h2>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {interactions.length}{" "}
                {interactions.length > 1 ? "interactions" : "interaction"}
              </span>
            </div>
            <NewInteractionForm
              prospectId={prospect.id}
              onCreated={refresh}
            />
            <div className="mt-6 space-y-3">
              {interactions.length === 0 && (
                <p className="text-sm text-[var(--color-muted-foreground)] text-center py-6">
                  Pas encore d'interaction enregistrée.
                </p>
              )}
              {interactions.map((it) => (
                <div
                  key={it.id}
                  className="flex gap-3 p-3 rounded-[var(--radius-md)] hover:bg-[var(--color-surface-2)] group transition-colors"
                >
                  <div className="flex flex-col items-center pt-1 flex-shrink-0">
                    <div className="h-2 w-2 rounded-full bg-[var(--color-coral-500)]" />
                    <div className="w-px flex-1 bg-[var(--color-border)] mt-1.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-[var(--color-navy-700)] uppercase tracking-wide">
                          {INTERACTION_KIND_LABELS[it.kind]}
                        </span>
                        <span className="text-xs text-[var(--color-muted-foreground)]">
                          {formatDateLong(it.occurredAt)}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon btn-sm opacity-0 group-hover:opacity-100"
                        aria-label="Supprimer"
                        onClick={async () => {
                          if (!confirm("Supprimer cette interaction ?")) return;
                          await api.deleteInteraction(it.id);
                          refresh();
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                      {it.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Éditer le prospect"
        size="lg"
      >
        <ProspectForm
          initial={prospect}
          submitLabel="Sauvegarder"
          onSubmit={async (values) => {
            await api.updateProspect(prospect.id, values);
            toast.success("Mise à jour enregistrée");
            setEditOpen(false);
            refresh();
          }}
        />
      </Modal>

      <Modal
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        title={`Close ${fullName(prospect)}`}
        description="Crée la vente et son échéancier de paiement"
        size="lg"
      >
        <CloseSaleForm
          defaultProduct={prospect.productInterest ?? null}
          onSubmit={async (values) => {
            await api.createSale({
              prospectId: prospect.id,
              ...values,
            });
            toast.success("Vente enregistrée");
            setCloseOpen(false);
            refresh();
          }}
        />
      </Modal>

      {issue && (
        <OutcomeModal
          open
          prospect={issue.prospect}
          eventId={issue.eventId}
          eventStart={issueEventStart}
          initialChoice={issue.choice}
          bookingUrl={bookingUrl}
          lastSaleProduct={sales[0]?.product ?? null}
          onClose={() => setIssue(null)}
          onDone={refresh}
        />
      )}
    </>
  );
}

function NewInteractionForm({
  prospectId,
  onCreated,
}: {
  prospectId: number;
  onCreated: () => void;
}) {
  const [kind, setKind] =
    useState<(typeof INTERACTION_KINDS)[number]>("note");
  const [body, setBody] = useState("");
  const [fieldKey, setFieldKey] = useState(0);
  const bodyField = useRef<HTMLTextAreaElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Valeur réelle du champ : un outil de dictée peut y écrire sans prévenir React.
    const text = (bodyField.current?.value ?? body).trim();
    if (!text) return;
    setSubmitting(true);
    try {
      await api.createInteraction({ prospectId, kind, body: text });
      setBody("");
      setFieldKey((k) => k + 1);
      setKind("note");
      onCreated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex items-center gap-2">
        <select
          className="input-base sm:max-w-36"
          value={kind}
          onChange={(e) =>
            setKind(e.target.value as (typeof INTERACTION_KINDS)[number])
          }
        >
          {INTERACTION_KINDS.map((k) => (
            <option key={k} value={k}>
              {INTERACTION_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </div>
      <DictationTextarea
        key={fieldKey}
        ref={bodyField}
        className="input-base"
        initialValue=""
        onValueChange={setBody}
        placeholder="Ce qui s'est passé et la prochaine étape..."
        rows={3}
      />
      <div className="flex justify-end">
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={submitting || !body.trim()}
        >
          <Plus size={14} /> Ajouter
        </button>
      </div>
    </form>
  );
}
